import type { StepDefinition } from './onboarding.types';

/**
 * Definición de los pasos. El orden importa: es el orden sugerido,
 * aunque el usuario puede completarlos libremente.
 *
 * Los pasos no son requisitos técnicos para emitir (existen "Productos Varios"
 * y "Clientes Varios"), sino la personalización mínima para que la primera
 * venta salga con datos propios. El copy explica ese beneficio y nunca
 * ofrece los genéricos como alternativa.
 *
 * La ruta de "company" lleva el placeholder {empresaId}, resuelto en
 * tiempo real por resolveStepRoute con la empresa activa. "product" y
 * "customer" reutilizan la convención ?action=create ya usada por el
 * sidebar (ver Layout.tsx: onCrearProducto/onCrearCliente).
 */
export const ONBOARDING_STEPS: StepDefinition[] = [
  {
    key: 'company',
    title: 'Completa los datos de tu empresa',
    shortTitle: 'Datos de la empresa',
    description: 'Tu logo, régimen y representante legal salen impresos en cada comprobante.',
    action: 'Completar datos',
    nextLabel: 'Completa tu empresa',
    unlockHint: 'Se activa al completar los datos de tu empresa',
    route: '/configuracion/empresa/{empresaId}',
  },
  {
    key: 'product',
    title: 'Agrega tu primer producto',
    shortTitle: 'Primer producto',
    description: 'Con su nombre y precio, para no emitir con «Productos Varios».',
    action: 'Agregar producto',
    nextLabel: 'Agrega tu producto',
    unlockHint: 'Se activa al agregar tu primer producto',
    route: '/productos?action=create',
  },
  {
    key: 'customer',
    title: 'Registra tu primer cliente',
    shortTitle: 'Primer cliente',
    description: 'Así el comprobante sale a su nombre y queda listo para tu próxima venta.',
    action: 'Registrar cliente',
    nextLabel: 'Registra tu cliente',
    unlockHint: 'Se activa al registrar tu primer cliente',
    route: '/clientes?action=create',
  },
];

/** Reemplaza el placeholder {empresaId} de una ruta del checklist. */
export function resolveStepRoute(route: string, empresaId: string): string {
  return route.replace('{empresaId}', empresaId);
}

/** Total de hitos del anillo: los 3 pasos + la emisión. */
export const TOTAL_MILESTONES = 4;

/** Días sin completar ningún paso tras los cuales el lanzador deja de mostrarse (queda en Ayuda). */
export const STALE_AFTER_DAYS = 14;

/**
 * Rutas donde el lanzador no aparece: el usuario ya está emitiendo y ahí
 * actúan los recordatorios dentro del formulario (OnboardingFieldHint).
 * Son las rutas reales de emisión y de punto de venta (ver privateRoutes.tsx).
 */
export const HIDE_ON_ROUTES = ['/comprobantes/emision', '/punto-venta'];

/**
 * Todos los textos en un solo lugar para facilitar ajustes de copy.
 */
export const COPY = {
  /** Título del popover. No incluye el nombre de la empresa a propósito:
   *  las razones sociales son de largo impredecible. */
  title: (isFirstCompany: boolean) =>
    isFirstCompany ? 'Tu primera venta' : 'Primera venta de esta empresa',

  /** Contador del popover. Cuenta los 4 hitos, la emisión incluida. */
  remaining: (n: number) => (n === 1 ? 'Falta 1 paso' : `Faltan ${n} pasos`),

  ready: 'Ya puedes emitir',

  goalTitle: 'Emite tu primera venta',
  goalLocked: 'Se activa al completar los pasos',
  goalReady: 'Todo listo para emitir',
  goalAction: 'Emitir comprobante',

  hide: 'Ocultar',
  /** Aviso tras ocultar: dice dónde recuperarlo justo cuando sirve. */
  hiddenNotice: 'Checklist oculto. Puedes volver a abrirlo desde Ayuda.',
  close: 'Cerrar',

  /** Lanzador: dice qué hacer, no cuánto falta (eso ya lo muestra el anillo). */
  launcher: {
    nextEyebrow: 'Siguiente paso',
    readyEyebrow: 'Todo listo',
    readyLabel: 'Emite tu primera venta',
  },

  /** Recordatorios dentro del formulario de emisión / POS. */
  fieldHint: {
    customer: {
      text: 'Registra tu primer cliente para que el comprobante salga a su nombre.',
      action: 'Registrar cliente',
    },
    product: {
      text: 'Agrega tu primer producto para no emitir con «Productos Varios».',
      action: 'Agregar producto',
    },
  },

  celebration: {
    title: 'Listo, ya estás vendiendo con Senciyo',
    body: (_companyName: string) =>
      'Tu primer comprobante fue emitido correctamente. Ya completaste los pasos básicos para empezar a vender. ¡Ahora puedes seguir haciendo crecer tu negocio!',
    recap: ['Empresa', 'Producto', 'Cliente', 'Venta'],
    action: 'Empezar a vender',
  },

  /** Aviso breve para la segunda empresa en adelante. */
  secondaryToast: (companyName: string) => `${companyName} ya está emitiendo`,
} as const;
